/**
 * Workspace Type Detector
 * Determines which coding workspace to load based on question metadata
 */

export const WORKSPACE_TYPES = {
  TYPESCRIPT: 'typescript',
  JAVASCRIPT: 'javascript',
  REACT: 'react',
  VUE: 'vue',
  ANGULAR: 'angular',
  NEXTJS: 'nextjs',
  HTML_CSS: 'html_css',
  PYTHON: 'python',
  JAVA: 'java',
  CPP: 'cpp',
  CSHARP: 'csharp',
  GO: 'go',
};

/**
 * Detects workspace type from question metadata
 * @param {Object} question - Question object containing workspaceType, compiler, or skill metadata
 * @returns {string} - Workspace type identifier
 */
export function detectWorkspaceType(question) {
  if (!question) return WORKSPACE_TYPES.HTML_CSS;

  // Priority 1: Explicit workspaceType field
  if (question.workspaceType) {
    return normalizeWorkspaceType(question.workspaceType);
  }

  // Priority 2: Compiler field
  if (question.compiler) {
    return normalizeWorkspaceType(question.compiler);
  }

  // Priority 3: _workspace field (backend internal)
  if (question._workspace) {
    return normalizeWorkspaceType(question._workspace);
  }

  // Priority 4: Infer from skill/topic
  const skill = (question.skill || question.topic || question._canonicalSkill || '').toLowerCase();

  if (skill.includes('typescript')) return WORKSPACE_TYPES.TYPESCRIPT;
  if (skill.includes('javascript') || skill.includes('js')) return WORKSPACE_TYPES.JAVASCRIPT;
  if (skill.includes('react')) return WORKSPACE_TYPES.REACT;
  if (skill.includes('vue')) return WORKSPACE_TYPES.VUE;
  if (skill.includes('angular')) return WORKSPACE_TYPES.ANGULAR;
  if (skill.includes('next')) return WORKSPACE_TYPES.NEXTJS;
  if (skill.includes('python')) return WORKSPACE_TYPES.PYTHON;
  if (skill.includes('java')) return WORKSPACE_TYPES.JAVA;
  if (skill.includes('c++') || skill.includes('cpp')) return WORKSPACE_TYPES.CPP;
  if (skill.includes('c#') || skill.includes('csharp')) return WORKSPACE_TYPES.CSHARP;
  if (skill.includes('go') || skill.includes('golang')) return WORKSPACE_TYPES.GO;
  if (skill.includes('html') || skill.includes('css')) return WORKSPACE_TYPES.HTML_CSS;

  // Default: HTML/CSS workspace
  return WORKSPACE_TYPES.HTML_CSS;
}

/**
 * Normalizes workspace type string to standard format
 */
function normalizeWorkspaceType(type) {
  const normalized = type.toLowerCase().replace(/[_-]/g, '');

  if (normalized.includes('typescript') || normalized === 'ts') return WORKSPACE_TYPES.TYPESCRIPT;
  if (normalized.includes('javascript') || normalized === 'js') return WORKSPACE_TYPES.JAVASCRIPT;
  if (normalized.includes('react')) return WORKSPACE_TYPES.REACT;
  if (normalized.includes('vue')) return WORKSPACE_TYPES.VUE;
  if (normalized.includes('angular')) return WORKSPACE_TYPES.ANGULAR;
  if (normalized.includes('next')) return WORKSPACE_TYPES.NEXTJS;
  if (normalized.includes('html') || normalized.includes('css')) return WORKSPACE_TYPES.HTML_CSS;
  if (normalized.includes('python') || normalized === 'py') return WORKSPACE_TYPES.PYTHON;
  if (normalized.includes('java')) return WORKSPACE_TYPES.JAVA;
  if (normalized.includes('cpp') || normalized === 'c++') return WORKSPACE_TYPES.CPP;
  if (normalized.includes('csharp') || normalized === 'c#') return WORKSPACE_TYPES.CSHARP;
  if (normalized.includes('go') || normalized.includes('golang')) return WORKSPACE_TYPES.GO;

  return type; // Return as-is if no match
}

/**
 * Validates if question workspace type matches expected skill
 * @param {Object} question - Question object
 * @param {string} expectedSkill - Expected skill/technology
 * @returns {Object} - { valid: boolean, detectedType: string, expectedType: string }
 */
export function validateWorkspaceMatch(question, expectedSkill) {
  const detectedType = detectWorkspaceType(question);
  const expectedType = normalizeWorkspaceType(expectedSkill);

  const valid = detectedType === expectedType;

  return {
    valid,
    detectedType,
    expectedType,
    message: valid
      ? 'Workspace matches question type'
      : `Mismatch: Question requires ${detectedType} but ${expectedType} workspace expected`
  };
}

/**
 * Gets display name for workspace type
 */
export function getWorkspaceDisplayName(workspaceType) {
  const names = {
    [WORKSPACE_TYPES.TYPESCRIPT]: 'TypeScript',
    [WORKSPACE_TYPES.JAVASCRIPT]: 'JavaScript',
    [WORKSPACE_TYPES.REACT]: 'React',
    [WORKSPACE_TYPES.VUE]: 'Vue.js',
    [WORKSPACE_TYPES.ANGULAR]: 'Angular',
    [WORKSPACE_TYPES.NEXTJS]: 'Next.js',
    [WORKSPACE_TYPES.HTML_CSS]: 'HTML & CSS',
    [WORKSPACE_TYPES.PYTHON]: 'Python',
    [WORKSPACE_TYPES.JAVA]: 'Java',
    [WORKSPACE_TYPES.CPP]: 'C++',
    [WORKSPACE_TYPES.CSHARP]: 'C#',
    [WORKSPACE_TYPES.GO]: 'Go',
  };

  return names[workspaceType] || workspaceType;
}
